import { Float } from '@adornis/baseql/baseqlTypes.js';
import { Entity, Field } from '@adornis/baseql/decorators.js';
import type { Maybe } from '@adornis/base/utilTypes.js';
import { PendingTask, registerPendingTaskDescBuilder } from './pending-task.js';

export const ONLINE_SUBMISSION_RECEIVED_TASK_CLASS = 'OnlineSubmissionReceivedPendingTask';

export interface OnlineSubmissionReceivedTaskOptions {
  studentName: string;
  /** A resubmission replaced an earlier online hand-in rather than creating the first one — see
   * `onlineSubmissionId` in student-exam.ts. */
  isResubmission: boolean;
  answeredCount: number;
  totalCount: number;
}

/**
 * Registered entity so the polymorphic examState.pendingTasks array can be hydrated. The raw document is
 * still pushed via createOnlineSubmissionReceivedPendingTask below; this just gives it a constructor in the
 * metaDataStore, same as every other PendingTask subtype.
 */
@Entity()
export class OnlineSubmissionReceivedPendingTask extends PendingTask {
  static override _class = ONLINE_SUBMISSION_RECEIVED_TASK_CLASS;

  @Field(() => String)
  priority: Maybe<string>;

  @Field(() => String)
  studentName: Maybe<string>;

  @Field(() => Boolean)
  isResubmission: Maybe<boolean>;

  @Field(() => Float)
  answeredCount: Maybe<number>;

  @Field(() => Float)
  totalCount: Maybe<number>;
}

/** Creates the raw document pushed to examState.pendingTasks in MongoDB. */
export function createOnlineSubmissionReceivedPendingTask(
  options: OnlineSubmissionReceivedTaskOptions,
): Record<string, unknown> {
  return {
    _class: ONLINE_SUBMISSION_RECEIVED_TASK_CLASS,
    priority: 'workflow',
    studentName: options.studentName,
    isResubmission: options.isResubmission,
    answeredCount: options.answeredCount,
    totalCount: options.totalCount,
  };
}

registerPendingTaskDescBuilder(ONLINE_SUBMISSION_RECEIVED_TASK_CLASS, task => {
  const t = task as unknown as Record<string, unknown>;
  const studentName = String(t.studentName ?? '') || 'Der Nutzer';
  const isResubmission = Boolean(t.isResubmission);
  const answeredCount = Number(t.answeredCount ?? 0);
  const totalCount = Number(t.totalCount ?? 0);

  // A RESUBMISSION gets a short, plain acknowledgment only — the full "next steps" checklist (generate a
  // master solution, then a grading scheme, then grade) was already said on the FIRST submission, and
  // repeating that whole speech every time a student redraws and hands the same exam back in again reads
  // as if the system forgot it already told them. The instruction itself carries this distinction (not
  // just the intro line above it) precisely so the agent doesn't reconstruct the checklist on its own.
  if (isResubmission) {
    return `### WORKFLOW TASK: Online-Abgabe erneut eingegangen [${ONLINE_SUBMISSION_RECEIVED_TASK_CLASS}]
${studentName} hat die online ausgefüllte Klausur erneut abgegeben — die vorherige Abgabe wurde durch diese ersetzt (und wird, falls bereits bewertet, automatisch neu bewertet (nur sagen wenn es zutrifft)).
- Beantwortete Aufgaben: ${answeredCount}/${totalCount}

Bestätige dem Nutzer NUR KURZ, dass die aktualisierte Abgabe eingegangen ist und die vorherige ersetzt hat. Wiederhole die Schritte-Übersicht (Musterlösung → Bewertungsschema → Bewertung) NICHT erneut — die wurde bereits bei der ersten Abgabe genannt.`;
  }

  return `### WORKFLOW TASK: Online-Abgabe eingegangen [${ONLINE_SUBMISSION_RECEIVED_TASK_CLASS}]
${studentName} hat die Klausur online ausgefüllt und soeben abgegeben.
- Beantwortete Aufgaben: ${answeredCount}/${totalCount}

Bestätige dem Nutzer aktiv den Eingang der Abgabe. Schlage danach nächste Sinnvolle Schritte vor.`;
});
